/**
 * Card receivables ageing (ADR-012): the card money each acquirer still owes, by how many days it is
 * past the date the acquirer was expected to pay it. Money not yet due is shown apart, so the late
 * buckets only hold money that really is late.
 */
import { useCallback, useEffect, useState } from 'react';
import { Hourglass, RefreshCw } from 'lucide-react';

import { cardClearingApi } from '@/api/cardClearing.api';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import type { CardReceivablesAgeingRowDto } from '@/types/cardReconciliation.types';

const BUCKETS: { key: keyof CardReceivablesAgeingRowDto; label: string; late: boolean }[] = [
  { key: 'notYetDue',  label: 'Not yet due', late: false },
  { key: 'days0To3',   label: '0 to 3 days',  late: false },
  { key: 'days4To7',   label: '4 to 7 days',  late: true },
  { key: 'days8To15',  label: '8 to 15 days', late: true },
  { key: 'days16To30', label: '16 to 30 days', late: true },
  { key: 'over30Days', label: 'Over 30 days', late: true },
];

const money = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

export const CardReceivablesAgeingPage = () => {
  const [asOf, setAsOf]       = useState(today());
  const [rows, setRows]       = useState<CardReceivablesAgeingRowDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async (date: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await cardClearingApi.getAgeing(date);
      setRows(res.data?.data ?? []);
    } catch {
      setError('The ageing could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(asOf); }, [asOf, load]);

  const total = (key: keyof CardReceivablesAgeingRowDto) =>
    rows.reduce((sum, r) => sum + (r[key] as number), 0);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Hourglass size={20} className="text-teal-600 dark:text-teal-400" /> Card Receivables Ageing
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Card sales the acquirers have not paid yet, by days past the expected settlement date.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-600 dark:text-slate-300">As at</label>
            <input type="date" value={asOf} onChange={e => setAsOf(e.target.value || today())}
              className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white" />
            <button onClick={() => void load(asOf)} title="Refresh"
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {error && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Hourglass size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">Every card sale has been settled.</p>
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Acquirer</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Sales</th>
                  {BUCKETS.map(b => (
                    <th key={b.key} className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{b.label}</th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {rows.map(r => (
                  <tr key={r.cardAcquirerId}>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{r.acquirerName}</td>
                    <td className="px-4 py-3 text-right text-slate-500 dark:text-slate-400">{r.itemCount}</td>
                    {BUCKETS.map(b => {
                      const v = r[b.key] as number;
                      return (
                        <td key={b.key} className={cn('px-4 py-3 text-right font-mono',
                          v === 0 ? 'text-slate-300 dark:text-slate-600'
                            : b.late ? 'text-orange-700 dark:text-orange-400' : 'text-slate-700 dark:text-slate-300')}>
                          {money(v)}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900 dark:text-white">{money(r.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-slate-200 dark:border-slate-600">
                <tr className="font-semibold">
                  <td className="px-4 py-3 text-slate-900 dark:text-white">All acquirers</td>
                  <td className="px-4 py-3 text-right text-slate-500 dark:text-slate-400">{rows.reduce((s, r) => s + r.itemCount, 0)}</td>
                  {BUCKETS.map(b => (
                    <td key={b.key} className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{money(total(b.key))}</td>
                  ))}
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{money(total('total'))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
